/**
 * One node type, five sub-pages.
 *
 * Properties, the relationships it takes part in, and the three rule families
 * that turn its source rows into one trustworthy record: data quality, matching
 * and survivorship. They are tabs over one thing, so they load in one request.
 */

import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { CopyButton, ErrorNotice, Loading } from '@/shared/components'
import { Chip, Mark, SubTabs } from '../components'
import { DqPanel } from './subpages/DqPanel'
import { EdgesPanel } from './subpages/EdgesPanel'
import { MatchPanel } from './subpages/MatchPanel'
import { PropertiesPanel } from './subpages/PropertiesPanel'
import { SurvivorshipPanel } from './subpages/SurvivorshipPanel'
import { useGraphModel, useNodeTypeDetail } from '../hooks'
import '../styles/brain.css'

type SubPage = 'properties' | 'edges' | 'dq' | 'match' | 'survivorship'

export function NodeDetailPage() {
  const { graphId = '', nodeTypeId = '' } = useParams<{ graphId: string; nodeTypeId: string }>()
  const [params, setParams] = useSearchParams()
  const navigate = useNavigate()

  const detail = useNodeTypeDetail(graphId, nodeTypeId)
  const model = useGraphModel(graphId)

  const sub = (params.get('sub') as SubPage) ?? 'properties'
  const setSub = (next: SubPage) => {
    const updated = new URLSearchParams(params)
    updated.set('sub', next)
    setParams(updated, { replace: true })
  }

  if (detail.isLoading) return <Loading label="Loading the node type…" />
  if (detail.error)
    return (
      <div className="page">
        <ErrorNotice error={detail.error} onRetry={detail.refetch} />
      </div>
    )
  if (!detail.data) return null

  const { node_type: node, counts } = detail.data

  return (
    <div className="cp-screen">
      <div className="cp-scroll">
      <button
        className="btn btn-ghost btn-sm"
        style={{ marginBottom: 10 }}
        onClick={() => navigate(`/brain/${graphId}?tab=nodes`)}
      >
        ← All node types
      </button>

      <div className="cp-toolbar">
        <Mark name={node.key} id={node.node_type_id} size={32} />
        <h1>{node.key}</h1>
        <Chip tone={node.category}>{node.category}</Chip>
        <span className="cp-spacer" />
        <div className="cp-card" style={{ minWidth: 260 }}>
          <div className="cp-kpis" style={{ borderBottom: 'none' }}>
            <div className="cp-kpi">
              <b>{counts.records.toLocaleString()}</b>
              <span>records</span>
            </div>
            <div className="cp-kpi">
              <b>{counts.properties}</b>
              <span>props</span>
            </div>
            <div className="cp-kpi">
              <b>{counts.dq_rules + counts.match_rules + counts.survivorship_rules}</b>
              <span>rules</span>
            </div>
          </div>
        </div>
        <div className="sub2">
          {node.description}
          {/* The id every API call and every object row is keyed by. */}
          <span className="row-tight small faint" style={{ marginTop: 6 }}>
            <span>node_type_id</span>
            <code className="mono">{node.node_type_id}</code>
            <CopyButton value={node.node_type_id} label="Copy" />
          </span>
        </div>
      </div>

      <SubTabs<SubPage>
        value={sub}
        onChange={setSub}
        tabs={[
          { key: 'properties', label: 'Properties', count: counts.properties },
          { key: 'edges', label: 'Edges', count: counts.edges },
          { key: 'dq', label: 'Data quality', count: counts.dq_rules },
          { key: 'match', label: 'Match rules', count: counts.match_rules },
          { key: 'survivorship', label: 'Survivorship', count: counts.survivorship_rules },
        ]}
      />

      <div style={{ marginTop: 16 }}>
        {sub === 'properties' && (
          <PropertiesPanel graphId={graphId} nodeTypeId={nodeTypeId} detail={detail.data} />
        )}
        {sub === 'edges' && (
          <EdgesPanel
            graphId={graphId}
            nodeTypeId={nodeTypeId}
            detail={detail.data}
            nodeTypes={model.data?.node_types ?? []}
          />
        )}
        {sub === 'dq' && (
          <DqPanel graphId={graphId} nodeTypeId={nodeTypeId} detail={detail.data} />
        )}
        {sub === 'match' && (
          <MatchPanel graphId={graphId} nodeTypeId={nodeTypeId} detail={detail.data} />
        )}
        {sub === 'survivorship' && (
          <SurvivorshipPanel graphId={graphId} nodeTypeId={nodeTypeId} detail={detail.data} />
        )}
      </div>
      </div>
    </div>
  )
}
